import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box,
    TextField,
    Button,
    Typography,
    Container,
    Link,
    IconButton,
    Alert,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { colors, typography } from '../theme/tokens';
import { DEMO_MODE, DEMO_CREDENTIALS } from '../utils/demoAuth';
import { setRegisterNumber } from '../redux/slices/authSlice';
import { sendOTPAPI } from '../services/authService';
import authBgImage from '../assets/images/auth-bg.png';
import Logo from '../assets/images/digilogo-b.svg';
import Onboarding1 from '../assets/images/onboarding_1.svg';
import Onboarding2 from '../assets/images/onboarding_2.svg';
import Onboarding3 from '../assets/images/onboarding_3.svg';
import { RiArrowRightCircleFill, RiPhoneLine } from '@remixicon/react';

// Country codes list
const countryCodes = [
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+1', country: 'US', flag: '🇺🇸' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'SA', flag: '🇸🇦' },
    { code: '+20', country: 'EG', flag: '🇪🇬' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+31', country: 'NL', flag: '🇳🇱' },
    { code: '+32', country: 'BE', flag: '🇧🇪' },
    { code: '+41', country: 'CH', flag: '🇨🇭' },
    { code: '+43', country: 'AT', flag: '🇦🇹' },
    { code: '+46', country: 'SE', flag: '🇸🇪' },
    { code: '+47', country: 'NO', flag: '🇳🇴' },
    { code: '+45', country: 'DK', flag: '🇩🇰' },
    { code: '+358', country: 'FI', flag: '🇫🇮' },
    { code: '+48', country: 'PL', flag: '🇵🇱' },
    { code: '+351', country: 'PT', flag: '🇵🇹' },
    { code: '+353', country: 'IE', flag: '🇮🇪' },
];

// Onboarding slides data
const onboardingSlides = [
    {
        title: 'Magic Menu Maker',
        description:
            'Unleash Your Creativity With Our Easy-To-Use Canvas. Design Menus As Colorful Or Classic As You Like—No Coding, Just Pure Imagination!',
        icon: <img src={Onboarding1} alt="Onboarding 1" />,
    },
    {
        title: 'Screen Harmony',
        description:
            'Link Up Your Screens And Watch Your Menu Come Alive Everywhere—One Big, Beautiful Display, Synced Perfectly Across Your Restaurant.',
        icon: <img src={Onboarding2} alt="Onboarding 2" />,
    },
    {
        title: 'Fast, Free Setup',
        description:
            'Getting Started Is As Simple As Pie. Free Installation Means You\'re Up And Running In Minutes, Zero Headaches.',
        icon: <img src={Onboarding3} alt="Onboarding 3" />,
    },
];

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [registerNumber, setRegisterNumberLocal] = useState('');
    const [countryCode, setCountryCode] = useState('+44'); // Default to UK
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Auto-advance slides
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % onboardingSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!registerNumber.trim()) return;

        setIsLoading(true);
        setError(null);

        try {
            // Format phone number with country code
            const fullPhoneNumber = `0${registerNumber.replace(/\s/g, '')}`;
            
            // Call the login API to send OTP
            const result = await sendOTPAPI(fullPhoneNumber, 'sms', 'united kingdom', 'English (United Kingdom)');
            
            if (result.success) {
                // Store register number with country code in Redux
                dispatch(setRegisterNumber(fullPhoneNumber));
                
                // Extract OTP length from response (default to 6 if not provided)
                const otpLength = result.data?.length || result.data?.length || 6;
                
                // Navigate to OTP page with full phone number and OTP length
                navigate('/otp', { 
                    state: { 
                        locale: 'united kingdom',
                        registerNumber: fullPhoneNumber, 
                        countryCode,
                        otpLength 
                    } 
                });
            } else {
                // Extract error message from result - handle both string and object errors
                let errorMessage = 'Failed to send OTP. Please try again.';
                
                if (typeof result.error === 'string') {
                    errorMessage = result.error;
                } else if (result.error?.message) {
                    errorMessage = result.error.message;
                } else if (result.error?.error) {
                    errorMessage = result.error.error;
                } else if (result.data?.message) {
                    errorMessage = result.data.message;
                } else if (result.data?.error) {
                    errorMessage = typeof result.data.error === 'string' 
                        ? result.data.error 
                        : result.data.error?.message || errorMessage;
                }
                
                setError(errorMessage);
            }
        } catch (error) {
            console.error('Login error:', error);
            // Extract error message from caught error
            const errorMessage = error?.response?.data?.message 
                || error?.response?.data?.error 
                || error?.message 
                || 'An error occurred. Please try again.';
            setError(typeof errorMessage === 'string' ? errorMessage : 'An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                bgcolor: colors.background.default,
            }}
        >
            {/* Left Side - Onboarding Carousel */}
            <Box
                sx={{
                    flex: 1,
                    backgroundImage: `url(${authBgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 6,
                    color: 'white',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        // background: 'rgba(0, 0, 0, 0.2)',
                        zIndex: 0,
                    },
                    '& > *': {
                        position: 'relative',
                        zIndex: 1,
                    },
                    margin: 1,
                    borderRadius: 2,
                }}
            >
                {/* Slide Content */}
                <Box
                    sx={{
                        maxWidth: 600,
                        textAlign: 'center',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        // backgroundColor: 'rgba(0,0,0,0.2)',
                        animation: 'fadeIn 0.5s ease-in',
                        '@keyframes fadeIn': {
                            from: { opacity: 0, transform: 'translateY(20px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            // fontWeight: typography.fontWeightExtraBold,
                            fontFamily: typography.fontFamilyHeading,
                            mb: 3,
                            fontSize: { xs: '2rem', md: '3rem' },
                            background: 'linear-gradient(90deg, rgba(246, 246, 246, 0.70) 8.63%, #FDEEF0 44.87%, rgba(255, 253, 253, 0.70) 109.13%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        {onboardingSlides[currentSlide].title}
                    </Typography>

                    {/* Icon/Illustration with Shadows */}
                    <Box
                        sx={{
                            mb: 6,
                            mt: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            filter: 'drop-shadow(0 30px 90px rgba(221, 34, 162, 0.40)) drop-shadow(0 50px 170px rgba(59, 32, 217, 0.50)) drop-shadow(0 120px 250px rgba(227, 27, 134, 0.53))',
                            // boxShadow: '0 30px 90px rgba(221, 34, 162, 0.20), 0 50px 170px rgba(59, 32, 217, 0.25), 0 120px 250px rgba(227, 27, 134, 0.33)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                filter: 'drop-shadow(0 35px 100px rgba(221, 34, 162, 0.25)) drop-shadow(0 60px 190px rgba(59, 32, 217, 0.30)) drop-shadow(0 140px 280px rgba(227, 27, 134, 0.38))',
                                // boxShadow: '0 35px 100px rgba(221, 34, 162, 0.25), 0 60px 190px rgba(59, 32, 217, 0.30), 0 140px 280px rgba(227, 27, 134, 0.38)',
                            },
                            '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                            },
                        }}
                    >
                        {onboardingSlides[currentSlide].icon}
                    </Box>

                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '1.25rem',
                            lineHeight: 1.8,
                            opacity: 0.95,
                            maxWidth: 540,
                            mx: 'auto',
                            fontFamily: typography.fontFamily,
                            fontWeight: typography.fontWeightRegular,
                            lineHeight: 1.2,
                            // color: colors.neutral.gray900,
                        }}
                    >
                        {onboardingSlides[currentSlide].description}
                    </Typography>
                </Box>

                {/* Slide Indicators */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 40,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    {onboardingSlides.map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            sx={{
                                width: currentSlide === index ? 48 : 32,
                                height: 4,
                                bgcolor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.3)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Right Side - Login Form */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, md: 6 },
                    bgcolor: 'white',
                }}
            >
                <Container maxWidth="sm">
                    <Box sx={{
                        p: 6,
                        mb: 6,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '2 solid #EFEFF0',
                        bgcolor: '#FDFDFD',
                        boxShadow: '0 24px 50px 0 rgba(0, 0, 0, 0.10)'
                    }}>
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <img src={Logo} alt="Logo" />
                        </Box>
                        {/* Main Content */}
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: typography.fontWeightBold,
                                    color: colors.neutral.gray900,
                                    mb: 2,
                                }}
                            >
                                Design Once. Display Everywhere.
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: colors.neutral.gray700,
                                    fontSize: '1.05rem',
                                }}
                            >
                                Design Menus, Link TVs, Update Instantly—No USBs.
                            </Typography>
                        </Box>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                {typeof error === 'string' 
                                    ? error 
                                    : error?.message || error?.error || 'An error occurred. Please try again.'}
                            </Alert>
                        )}

                        {/* Login Form */}
                        <Box component="form" onSubmit={handleLogin}
                            sx={{
                                // p: 6,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                            }}>
                            {/* Country Code Selector */}
                            <FormControl
                                sx={{
                                    minWidth: 120,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        bgcolor: colors.neutral.gray50,
                                        '& fieldset': {
                                            borderColor: '#EFEFF0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: colors.neutral.gray300,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3D3C4A',
                                        },
                                    },
                                }}
                            >
                                <Select
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    renderValue={(value) => {
                                        const selected = countryCodes.find(c => c.code === value);
                                        return selected ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>{selected.flag}</span>
                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                    {selected.code}
                                                </Typography>
                                            </Box>
                                        ) : value;
                                    }}
                                    sx={{
                                        height: '56px',
                                        '& .MuiSelect-select': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        },
                                    }}
                                >
                                    {countryCodes.map((country) => (
                                        <MenuItem key={country.code} value={country.code}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>{country.flag}</span>
                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                    {country.code}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                placeholder="Phone Number"
                                value={registerNumber}
                                onChange={(e) => {
                                    // Only allow digits and spaces
                                    const value = e.target.value.replace(/[^\d\s]/g, '');
                                    setRegisterNumberLocal(value);
                                }}
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                }}
                                sx={{
                                    // mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        bgcolor: colors.neutral.gray50,
                                        '& fieldset': {
                                            borderColor: '#EFEFF0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: colors.neutral.gray300,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3D3C4A',
                                        },
                                    },
                                }}
                            />

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={!registerNumber.trim() || isLoading}
                                sx={{
                                    bgcolor: colors.primary.main,
                                    color: 'white',
                                    py: 1.75,
                                    fontSize: '1.1rem',
                                    fontWeight: typography.fontWeightSemiBold,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    width: '10rem',
                                    '&:hover': {
                                        bgcolor: colors.primary.dark,
                                        boxShadow: 'none',
                                    },
                                    '&:disabled': {
                                        bgcolor: colors.neutral.gray300,
                                        color: colors.neutral.gray500,
                                    },
                                }}
                            >
                                {isLoading ? 'Sending...' : 'Log In'}
                            </Button>
                        </Box>

                        {/* Terms and Privacy */}
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 4,
                                textAlign: 'center',
                                color: colors.neutral.gray600,
                                fontSize: '0.9rem',
                            }}
                        >
                            Joining our app means you agree with our{' '}
                            <Link
                                href="#"
                                sx={{
                                    color: colors.primary.main,
                                    textDecoration: 'none',
                                    fontWeight: typography.fontWeightMedium,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Terms of Use
                            </Link>{' '}
                            and{' '}
                            <Link
                                href="#"
                                sx={{
                                    color: colors.primary.main,
                                    textDecoration: 'none',
                                    fontWeight: typography.fontWeightMedium,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Privacy Policy
                            </Link>
                        </Typography>
                    </Box>

                    {/* Support Section */}
                    <Box
                        sx={{
                            mt: 6,
                            p: 3,
                            bgcolor: 'linear-gradient(180deg, rgba(249, 209, 213, 0.05) 0%, rgba(226, 209, 249, 0.05) 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: '1px solid #C61F2F'
                        }}
                    >
                        <Box sx={{ alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RiPhoneLine size={24} color={colors.primary.main} />
                                {/* <PhoneIcon sx={{ color: colors.primary.main, mr: 1.5 }} /> */}
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: typography.fontWeightSemiBold,
                                        color: colors.neutral.gray900,
                                    }}
                                >
                                    24/7 Support
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: colors.neutral.gray600,
                                    fontSize: '0.85rem',
                                }}
                            >
                                Always-on help by chat and email.
                            </Typography>
                        </Box>
                        {/* <IconButton
                            sx={{
                                bgcolor: colors.primary.main,
                                color: 'white',
                                '&:hover': {
                                    bgcolor: colors.primary.dark,
                                },
                            }}
                        > */}
                        <RiArrowRightCircleFill size={24} color={colors.primary.main} />
                        {/* <PhoneIcon /> */}
                        {/* </IconButton> */}
                    </Box>
                </Container>
            </Box >
        </Box >
    );
};

export default Login;

